<template>
  <b-modal
    id="form-brand"
    :visible="shallShowBrandFormModal"
    :title="`${$t('Form')} ${$t('Brand')}`"
    modal-class="modal-sticky"
    footer-class="d-flex justify-content-between"
    body-class="p-0"
    size="lg"
    no-fade
    hide-backdrop
    static
    @change="(val) => $emit('update:shall-show-brand-form-modal', val)"
  >
    <!-- Modal Header -->
    <template #modal-header>
      <h5 class="modal-title">
        {{ `${$t(typeBrandForm)} ${$t('Brand')}` }}
      </h5>
      <div class="modal-actions">
        <feather-icon
          icon="MinusIcon"
          class="cursor-pointer"
          @click="(busy === false) ? $emit('update:shall-show-brand-form-modal', false) : false"
        />
        <!-- <feather-icon
          icon="Maximize2Icon"
          class="ml-1 cursor-pointer"
        /> -->
        <feather-icon
          icon="XIcon"
          class="ml-1 cursor-pointer"
          @click="(busy === false) ? onDiscard() : false"
        />
      </div>
    </template>

    <!-- Modal Footer -->
    <template #modal-footer>
      <!-- Footer: Left Content -->
      <div>
        <b-button
          v-if="(typeBrandForm !== 'View')"
          ref="submit"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          right
          :disabled="busy"
          @click="onSubmit"
        >
          {{ (typeBrandForm === 'Add' || typeBrandForm === 'Edit') ? $t('Save Data') : $t('Delete Data') }}
        </b-button>
      </div>

      <!-- Footer: Right Content -->
      <div>
        <!-- Discard -->
        <feather-icon
          icon="XCircleIcon"
          size="17"
          class="ml-75 cursor-pointer"
          @click="(busy === false) ? onDiscard() : false"
        />
      </div>
    </template>

    <!-- Modal: Body -->
    <validation-observer
      #default="{ handleSubmit }"
      ref="refFormObserver"
    >

      <div>
        <b-form
          ref="refForm"
          class="position-relative"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <div class="mx-1">
            <b-row>

              <!-- Field: Code -->
              <b-col
                cols="12"
                md="6"
                class="my-1"
              >
                <b-form-group
                  v-if="(typeBrandForm === 'Add' || typeBrandForm === 'Edit')"
                  :label="$t('Brand Code')"
                  label-for="brand-code"
                >
                  <validation-provider
                    #default="validationContext"
                    :name="$t('Brand Code')"
                    rules="required"
                  >
                    <b-form-input
                      id="brand-code"
                      v-model="brandData.code"
                      :state="getValidationState(validationContext)"
                      :disabled="busy"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>

                </b-form-group>

                <b-form-group
                  v-else
                  :label="$t('Brand Code')"
                  label-for="brand-code"
                >
                  <b-form-input
                    id="brand-code"
                    v-model="brandData.code"
                    :readonly="true"
                  />
                </b-form-group>

              </b-col>

              <!-- Field: Name -->
              <b-col
                cols="12"
                md="6"
                class="my-1"
              >
                <b-form-group
                  v-if="(typeBrandForm === 'Add' || typeBrandForm === 'Edit')"
                  :label="$t('Brand Name')"
                  label-for="brand-name"
                >
                  <validation-provider
                    #default="validationContext"
                    :name="$t('Brand Name')"
                    rules="required"
                  >
                    <b-form-input
                      id="brand-name"
                      v-model="brandData.name"
                      :state="getValidationState(validationContext)"
                      :disabled="busy"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>

                </b-form-group>

                <b-form-group
                  v-else
                  :label="$t('Brand Name')"
                  label-for="brand-name"
                >
                  <b-form-input
                    id="brand-name"
                    v-model="brandData.name"
                    :readonly="true"
                  />
                </b-form-group>

              </b-col>

            </b-row>

            <b-row>

              <!-- Field: Description -->
              <b-col
                cols="12"
                md="6"
                lg="6"
              >
                <b-form-group
                  v-if="(typeBrandForm === 'Add' || typeBrandForm === 'Edit')"
                  :label="$t('Description')"
                  label-for="description"
                >
                  <b-form-input
                    id="description"
                    v-model="brandData.description"
                    :disabled="busy"
                  />
                </b-form-group>

                <b-form-group
                  v-else
                  :label="$t('Description')"
                  label-for="description"
                >
                  <b-form-input
                    id="description"
                    v-model="brandData.description"
                    :readonly="true"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="6"
                lg="6"
              >
                <b-form-group
                  v-if="(typeBrandForm === 'Add' || typeBrandForm === 'Edit')"
                  :label="$t('Status')"
                  label-for="status"
                  label-class="mb-1"
                >
                  <b-form-radio-group
                    id="status"
                    v-model="brandData.status"
                    :options="statusOptions"
                    value="active"
                  />
                </b-form-group>

                <b-form-group
                  v-else
                  :label="$t('Status')"
                  label-for="status"
                >
                  <b-badge
                    pill
                    :variant="`light-${resolveBrandStatusVariantAndIcon(brandData.status).variant}`"
                    class="p-1"
                  >
                    <feather-icon
                      :icon="resolveBrandStatusVariantAndIcon(brandData.status).icon"
                    />
                    {{ $t(textFirstUpper(brandData.status)) }}
                  </b-badge>
                </b-form-group>
              </b-col>

            </b-row>
          </div>

        </b-form>

      </div>

    </validation-observer>

    <b-overlay
      :show="busy"
      no-wrap
      @shown="onShown"
      @hidden="onHidden"
    >
      <template #overlay>
        <div
          v-if="processing"
          class="text-center p-2 bg-primary text-light rounded"
        >
          <feather-icon
            icon="UploadCloudIcon"
            size="20"
          />
          <div class="mb-2">
            {{ `${$t('Processing')}...` }}
          </div>
          <b-progress
            min="1"
            max="10"
            :value="counter"
            variant="success"
            height="6px"
            class="mx-n1"
          />
        </div>
        <div
          v-else
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="text-center p-3"
        >
          <b-card-text
            class="font-weight-bolder"
          >
            {{ $t('Are you sure?') }}
          </b-card-text>
          <div class="d-flex">
            <b-button
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              variant="outline-danger"
              class="mr-3"
              @click="onCancel"
            >
              {{ $t('Cancel') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              variant="outline-success"
              @click="onOK"
            >
              {{ $t('Ok') }}
            </b-button>
          </div>
        </div>
      </template>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton, BForm, BFormGroup, BFormInput, BRow, BCol, BBadge,
  BFormRadioGroup,
  BFormInvalidFeedback,
  BOverlay,
  BProgress,
  BCardText,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {
  // ref,
  onUnmounted,
  // computed,
} from '@vue/composition-api'
import {
  ValidationProvider, ValidationObserver,
} from 'vee-validate'
import formValidation from '@core/comp-functions/forms/form-validation'
import { required } from '@validations'
import { t } from '@/@core/libs/i18n/utils'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import brandStoreModule from './brandStoreModule'
import useBrandList from './useBrandList'

export default {
  directives: {
    Ripple,
  },
  components: {
    // BSV
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BOverlay,
    BRow,
    BCol,
    BBadge,
    BFormRadioGroup,
    BFormInvalidFeedback,
    BProgress,
    BCardText,

    ValidationProvider,
    ValidationObserver,
  },
  model: {
    prop: 'shallShowBrandFormModal',
    event: 'update:shall-show-brand-form-modal',
  },
  props: {
    shallShowBrandFormModal: {
      type: Boolean,
      required: true,
    },
    typeBrandForm: {
      type: String,
      default: 'Add',
    },
    brandData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      required,
      busy: false,
      processing: false,
      counter: 1,
      interval: null,
    }
  },
  methods: {
    onShown() {
      // Focus the dialog prompt
      this.$refs.dialog.focus()
    },
    onHidden() {
      // In this case, we return focus to the submit button
      // You may need to alter this based on your application requirements
      this.$refs.submit.focus()
    },
    onSubmit() {
      this.$refs.refFormObserver.validate().then(success => {
        if (success) {
          this.processing = false
          this.busy = true
        }
      })
    },
    onCancel() {
      this.busy = false
    },
    onOK() {
      this.processing = true
      if (this.typeBrandForm === 'Add') {
        store.dispatch('brand-store/addBrand', this.brandData)
          .then(() => {
            const okMsg = this.$i18n.t('Process on successfully')
            this.$toast({
              component: ToastificationContent,
              props: {
                title: okMsg,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            },
            {
              position: 'bottom-right',
            })
            this.busy = false
            this.processing = false

            this.$emit('brand-form-update')
            this.$emit('update:shall-show-brand-form-modal', false)
          })
          .catch(error => {
            const { response, message } = error
            if (response) {
              const errorMsg = this.$i18n.t(response?.data.error)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: errorMsg,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            } else if (message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: error?.message,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            }
            this.busy = false
            this.processing = false
          })
      } else if (this.typeBrandForm === 'Edit') {
        store.dispatch('brand-store/editBrand', this.brandData)
          .then(() => {
            const okMsg = this.$i18n.t('Process on successfully')
            this.$toast({
              component: ToastificationContent,
              props: {
                title: okMsg,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            },
            {
              position: 'bottom-right',
            })
            this.busy = false
            this.processing = false

            this.$emit('brand-form-update')
            this.$emit('update:shall-show-brand-form-modal', false)
          })
          .catch(error => {
            const { response, message } = error
            if (response) {
              const errorMsg = this.$i18n.t(response?.data.error)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: errorMsg,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            } else if (message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: error?.message,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            }
            this.busy = false
            this.processing = false
          })
      } else if (this.typeBrandForm === 'Delete') {
        store.dispatch('brand-store/deleteBrand', {
          id: this.brandData.id,
        })
          .then(() => {
            const okMsg = this.$i18n.t('Process on successfully')
            this.$toast({
              component: ToastificationContent,
              props: {
                title: okMsg,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            },
            {
              position: 'bottom-right',
            })
            this.busy = false
            this.processing = false

            this.$emit('brand-form-update')
            this.$emit('update:shall-show-brand-form-modal', false)
          })
          .catch(error => {
            const { response, message } = error
            if (response) {
              const errorMsg = this.$i18n.t(response?.data.error)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: errorMsg,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            } else if (message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: error?.message,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              },
              {
                position: 'bottom-right',
              })
            }
            this.busy = false
            this.processing = false
          })
      }
    },
    onDiscard() {
      this.$emit('discard-brand-form')
      this.$emit('update:shall-show-brand-form-modal', false)
    },
  },
  setup() {
    const BRAND_STORE_MODULE_NAME = 'brand-store'

    // Register module
    if (!store.hasModule(BRAND_STORE_MODULE_NAME)) store.registerModule(BRAND_STORE_MODULE_NAME, brandStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(BRAND_STORE_MODULE_NAME)) store.unregisterModule(BRAND_STORE_MODULE_NAME)
    })

    const statusOptions = [
      { text: t('Active'), value: 'active' },
      { text: t('Inactive'), value: 'inactive' },
    ]

    // const blankBrand = {
    //   name: '',
    //   description: '',
    //   status: 'active',
    // }
    // const brandLocal = ref(JSON.parse(JSON.stringify(blankBrand)))
    // const brandLocal = computed(() => props.brandData)
    // const resetBrandLocal = () => {
    //   console.log(props.brandData)
    //   brandLocal.value = JSON.parse(JSON.stringify(blankBrand))
    // }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
      clearForm,
    } = formValidation()

    // const saveBrand = () => {
    //   // resetBrandLocal()
    //   emit('update:shall-show-brand-form-modal', false)
    // }

    // const discardBrand = () => {
    //   console.log('discard brand')
    //   // resetBrandLocal()
    //   emit('update:shall-show-brand-form-modal', false)
    // }

    const {
      textFirstUpper,
      resolveBrandStatusVariantAndIcon,
    } = useBrandList()

    return {
      // brandLocal,
      statusOptions,

      // Form Validation
      resetForm,
      clearForm,
      refFormObserver,
      getValidationState,

      // Form actions
      // saveBrand,
      // discardBrand,

      textFirstUpper,
      resolveBrandStatusVariantAndIcon,
    }
  },
}
</script>
